// One store, open: who they are, what to do now (with the words), the
// outcome buttons that move the row, who owns it, the notes and the log.
import React, { useEffect, useState } from 'react';
import { AtSign, Footprints, Globe, Mail, MapPin, MessageSquare, Pencil, Phone, RefreshCw, Trash2 } from 'lucide-react';
import { CADENCE, DEMO_STEPS, EXTRA_OUTCOME_LABEL, KIND_LABEL, STAGES, STAGE_ACTIONS, applyOutcome, currentAction, dueLabel, fillScript, isDoNotContact, moveToStage, stageOf, withTemp } from './salesPlan';
import { deleteProspect, e164, insertTouch, loadEmails, loadTouches, prettyPhone, updateProspect } from './salesData';
import { CallbackChip, DOT, Field, RepChip, Sheet, StagePill, TONE, TempPicker, TierChip, btnGhost, btnPrimary, btnSmall, inputCls } from './salesUi';
import ActionCard from './ActionCard';
import BriefCard from './BriefCard';
import ReplyDrafter from './ReplyDrafter';
import EmailComposer, { EmailThread, splitScript } from './EmailComposer';
import DealForm, { dealFromProspect, dealPatch, dealValid } from './DealForm';
import { dealEarnings, isDeal, money, planLabel } from './salesCommission';

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric', hour: 'numeric', minute: '2-digit' });
};
const mapsUrl = (p) => {
  const q = [p.name, p.address, p.city, p.state, p.zip].filter(Boolean).join(', ');
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : '';
};
const webUrl = (u) => (u ? (/^https?:\/\//i.test(u) ? u : `https://${u}`) : '');
const socialUrl = (s) => {
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  const h = s.replace(/^@/, '');
  return `https://www.instagram.com/${encodeURIComponent(h)}`;
};
export default function ProspectDrawer({ client, storeId, prospect: p, reps, repById, me, userEmail, onChange, onTouch, onDelete, onClose, notify, dealsReady = false, briefReady = false }) {
  const [replying, setReplying] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emails, setEmails] = useState([]);
  const [touches, setTouches] = useState([]);
  const [touchesLoading, setTouchesLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [notes, setNotes] = useState(p.notes || '');
  const [deal, setDeal] = useState(() => dealFromProspect(p, { me }));
  const [dealOpen, setDealOpen] = useState(false);

  const rep = repById?.[p.rep_id];
  const showDeal = dealsReady && (p.stage === 'customer' || p.stage === 'pilot' || isDeal(p));
  const earn = isDeal(p) ? dealEarnings(p) : null;
  useEffect(() => { setDeal(dealFromProspect(p, { me })); setDealOpen(false); }, [p.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const tel = e164(p.phone);
  const stage = stageOf(p.stage, p);

  useEffect(() => {
    let alive = true;
    setTouchesLoading(true);
    loadTouches(client, p.id)
      .then((rows) => { if (alive) setTouches(rows); })
      .catch((e) => { console.error('sales: touches failed', e); })
      .finally(() => { if (alive) setTouchesLoading(false); });
    return () => { alive = false; };
  }, [client, p.id]);

  useEffect(() => { setNotes(p.notes || ''); }, [p.id, p.notes]);
  useEffect(() => {
    let alive = true;
    setEmails([]); setEmailing(!!currentAction(p)?.isReply && !!p.email);
    loadEmails(client, p.id).then(({ rows }) => { if (alive) setEmails(rows); }).catch((e) => console.error('sales: emails failed', e));
    return () => { alive = false; };
  }, [client, p.id]);
  const action = currentAction(p);
  const emailScript = action?.kind === 'email' ? splitScript(fillScript(action.say, { prospect: p, rep: me })) : { subject: '', body: '' };
  const lastInbound = [...emails].reverse().find((m) => m.direction === 'in');

  const commit = async (patch, touch) => {
    setBusy(true);
    try {
      const row = await updateProspect(client, p.id, patch);
      onChange(row);
      if (touch) {
        const t = await insertTouch(client, { ...touch, store_id: storeId, prospect_id: p.id });
        setTouches((prev) => [t, ...prev]);
        onTouch?.(t);
      }
      return row;
    } catch (e) {
      console.error('sales: update failed', e);
      notify('Could not save that. Try again.', 'error');
      return null;
    } finally {
      setBusy(false);
    }
  };

  // The ActionCard handles the prompts (why lost, what number); this saves.
  const logOutcome = async (o, text, extra, opts = {}) => {
    if (busy) return null;
    const { patch, touch } = applyOutcome(p, o, { note: text, rep: me, when: opts.when || null, never: !!opts.never });
    if (extra) Object.assign(patch, extra);
    const row = await commit(patch, touch);
    if (row) {
      setNote('');
      const to = stageOf(row.stage, p).label;
      notify(row.stage !== p.stage ? `${p.name} moved to ${to}` : row.next_due_at ? `Logged. Next: ${row.next_action}, ${dueLabel(row.next_due_at).toLowerCase()}` : 'Logged');
    }
    return row;
  };

  const addNote = async () => {
    const text = note.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const t = await insertTouch(client, { store_id: storeId, prospect_id: p.id, kind: 'note', note: text, from_stage: p.stage, to_stage: p.stage, rep_id: me?.id || null, rep_name: me?.name || null, happened_at: new Date().toISOString() });
      setTouches((prev) => [t, ...prev]);
      onTouch?.(t);
      setNote('');
    } catch (e) {
      console.error('sales: note failed', e);
      notify('Could not save the note', 'error');
    } finally {
      setBusy(false);
    }
  };

  const changeRep = async (repId) => {
    const r = repById?.[repId];
    await commit({ rep_id: repId || null }, { kind: 'assign', note: r ? `Assigned to ${r.name}` : 'Unassigned', from_stage: p.stage, to_stage: p.stage, rep_id: me?.id || null, rep_name: me?.name || null, happened_at: new Date().toISOString() });
  };

  const changeStage = async (stageKey) => {
    if (stageKey === p.stage) return;
    const patch = moveToStage(p, stageKey);
    if (stageKey === 'customer' && dealsReady && !p.closer_rep_id) patch.closer_rep_id = p.rep_id || me?.id || null;
    await commit(patch, { kind: 'stage', note: `Moved to ${stageOf(stageKey, p).label} by hand`, from_stage: p.stage, to_stage: stageKey, rep_id: me?.id || null, rep_name: me?.name || null, happened_at: new Date().toISOString() });
  };

  // Facts the tidy-up pulled from a note: fill empty fields, never overwrite.
  const applyFacts = async (f) => {
    const patch = {};
    if (f.owner_name && !p.owner_name) patch.owner_name = String(f.owner_name).slice(0, 80);
    const extras = [f.decision_maker && `Decides: ${f.decision_maker}`, f.best_time && `Best time: ${f.best_time}`, f.competitor && `Uses: ${f.competitor}`].filter(Boolean);
    const cur = String(p.notes || '');
    const add = extras.filter((x) => !cur.includes(x));
    if (add.length) patch.notes = [cur.trim(), ...add].filter(Boolean).join('\n');
    if (Object.keys(patch).length) { const row = await commit(patch); if (row) setNotes(row.notes || ''); }
  };

  const briefTouch = async () => {
    try {
      const t = await insertTouch(client, { store_id: storeId, prospect_id: p.id, kind: 'note', outcome: 'brief', note: 'Built the brief', from_stage: p.stage, to_stage: p.stage, rep_id: me?.id || null, rep_name: me?.name || null, happened_at: new Date().toISOString() });
      setTouches((prev) => [t, ...prev]);
      onTouch?.(t);
    } catch (e) { console.error('sales: brief touch failed', e); }
  };

  const saveDeal = async () => {
    if (!dealValid(deal) || busy) return;
    const row = await commit(dealPatch(deal));
    if (row) { setDealOpen(false); notify('Deal saved. Commission updates on the Earnings view.'); }
  };

  const saveNotes = async () => {
    if ((p.notes || '') === notes) return;
    await commit({ notes: notes.trim() || null });
  };

  const startEdit = () => {
    setForm({ name: p.name || '', owner_name: p.owner_name || '', phone: p.phone || '', email: p.email || '', address: p.address || '', city: p.city || '', state: p.state || '', zip: p.zip || '', website: p.website || '', social: p.social || '' });
    setEditing(true);
  };
  const saveEdit = async () => {
    if (!form.name.trim()) return;
    const patch = {
      name: form.name.trim(),
      owner_name: form.owner_name.trim() || null,
      phone: e164(form.phone) || form.phone.trim() || null,
      email: form.email.trim() || null,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      zip: form.zip.trim() || null,
      website: form.website.trim() || null,
      social: form.social.trim() || null,
    };
    const row = await commit(patch);
    if (row) setEditing(false);
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${p.name} from the list? The log goes with it.`)) return;
    setBusy(true);
    try {
      await deleteProspect(client, p.id);
      notify(`${p.name} removed`);
      onDelete(p.id);
    } catch (e) {
      console.error('sales: delete failed', e);
      notify('Could not remove that store', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      width="lg"
      title={
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-lg font-bold text-slate-900 truncate">{p.name}</h2>
          <StagePill stage={p.stage} />
          <TierChip prospect={p} />
        </div>
      }
      subtitle={[p.owner_name && `Owner: ${p.owner_name}`, [p.city, p.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || 'No owner or city on file'}
    >
      {isDoNotContact(p) && (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          <span className="font-bold">Do not contact.</span> This store made it clear they want no software. No calls, no emails, no visits. It stays on the list only so it cannot be imported again.
        </div>
      )}
      {!isDoNotContact(p) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <TempPicker prospect={p} disabled={busy} onPick={(t) => commit({ tags: withTemp(p.tags, t), updated_at: new Date().toISOString() })} />
          <CallbackChip prospect={p} />
        </div>
      )}
      {/* Contact */}
      {!editing ? (
        <div className="flex flex-wrap items-center gap-2">
          {tel ? (
            <>
              <a href={`tel:${tel}`} className={btnSmall}><Phone className="w-3.5 h-3.5" /> {prettyPhone(p.phone)}</a>
              <a href={`sms:${tel}`} className={btnSmall} title="Text (only if they gave you this number)"><MessageSquare className="w-3.5 h-3.5" /> Text</a>
            </>
          ) : p.phone ? <span className={btnSmall}>{p.phone}</span> : null}
          {p.email && <button type="button" onClick={() => setEmailing((v) => !v)} className={`${btnSmall} ${emailing ? 'border-sky-300 bg-sky-50' : ''}`}><Mail className="w-3.5 h-3.5" /> Email</button>}
          {p.website && <a href={webUrl(p.website)} target="_blank" rel="noopener noreferrer" className={btnSmall}><Globe className="w-3.5 h-3.5" /> Website</a>}
          {p.social && <a href={socialUrl(p.social)} target="_blank" rel="noopener noreferrer" className={btnSmall}><AtSign className="w-3.5 h-3.5" /> Social</a>}
          {mapsUrl(p) && <a href={mapsUrl(p)} target="_blank" rel="noopener noreferrer" className={btnSmall}><MapPin className="w-3.5 h-3.5" /> Map</a>}
          <button type="button" onClick={startEdit} className={`${btnSmall} ml-auto`} title="Edit name, owner, phone, address"><Pencil className="w-3.5 h-3.5" /> Edit</button>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Store name" className="sm:col-span-2"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
          <Field label="Owner"><input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className={inputCls} /></Field>
          <Field label="Phone"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
          <Field label="Email" className="sm:col-span-2"><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
          <Field label="Address" className="sm:col-span-2"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></Field>
          <Field label="City"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="State"><input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} /></Field>
            <Field label="ZIP"><input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className={inputCls} /></Field>
          </div>
          <Field label="Website"><input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inputCls} /></Field>
          <Field label="Facebook or Instagram"><input value={form.social} onChange={(e) => setForm({ ...form, social: e.target.value })} className={inputCls} /></Field>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} className={btnGhost}>Cancel</button>
            <button type="button" onClick={saveEdit} disabled={busy || !form.name.trim()} className={btnPrimary}>Save</button>
          </div>
        </div>
      )}

      {/* History at a glance: how many times, by what, since when, and what they said */}
      <HistoryStrip p={p} touches={touches} loading={touchesLoading} />

      {/* Owner + stage */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Owned by">
          <div className="flex items-center gap-2">
            <RepChip rep={rep} size="md" />
            <select value={p.rep_id || ''} onChange={(e) => changeRep(e.target.value)} disabled={busy} className={inputCls}>
              <option value="">Nobody yet</option>
              {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </Field>
        <Field label="Stage" hint="Logging an outcome below moves it for you. This is for fixing a mistake.">
          <div className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-md flex-shrink-0 ${DOT[stage.tone]}`} />
            <select value={p.stage} onChange={(e) => changeStage(e.target.value)} disabled={busy} className={inputCls}>
              {STAGES.map((s) => <option key={s.key} value={s.key}>{stageOf(s.key, p).label}</option>)}
            </select>
          </div>
        </Field>
      </div>

      {/* Deal */}
      {showDeal && (
        <div className={`mt-4 rounded-2xl border p-4 ${isDeal(p) ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/60'}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[11px] font-bold uppercase tracking-wider ${isDeal(p) ? 'text-emerald-700' : 'text-amber-800'}`}>Deal</span>
            {!dealOpen && (
              <button type="button" onClick={() => setDealOpen(true)} className="ml-auto text-xs font-semibold text-blue-700 hover:underline">{isDeal(p) ? 'Edit' : 'Set it up'}</button>
            )}
          </div>
          {!dealOpen ? (
            isDeal(p) ? (
              <div className="mt-1 text-sm text-slate-800">
                <span className="font-bold">{money(p.monthly_amount)} a month</span> · {planLabel(p.plan)} · since {new Date(`${p.billing_start}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                {p.churned_at && <span className="text-red-700"> · stopped {new Date(`${p.churned_at}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                <div className="text-xs text-slate-600 mt-1">
                  Closed by <span className="font-semibold">{repById?.[p.closer_rep_id || p.rep_id]?.name || 'nobody'}</span>: {money(earn.earned)} earned over {earn.months} month{earn.months === 1 ? '' : 's'}{earn.active ? `, ${money(earn.residual)} a month from here` : ', no longer paying'}.
                </div>
              </div>
            ) : (
              <p className="mt-1 text-sm text-amber-900">{p.stage === 'customer' ? 'No amount on this customer yet, so no commission is counted. Set the plan and the first payment date.' : 'When the pilot converts, log it below and the deal form opens.'}</p>
            )
          ) : (
            <div className="mt-3">
              <DealForm value={deal} onChange={setDeal} reps={reps} />
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={() => { setDealOpen(false); setDeal(dealFromProspect(p, { me })); }} className={btnGhost}>Cancel</button>
                <button type="button" onClick={saveDeal} disabled={busy || !dealValid(deal)} className={btnPrimary}>Save deal</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Brief */}
      {p.stage !== 'lost' && (
        <div className="mt-4">
          <BriefCard client={client} storeId={storeId} prospect={p} me={me} briefReady={briefReady} notify={notify} onSaved={(patch) => commit(patch)} onBuilt={briefTouch} compact />
        </div>
      )}

      {/* Email: the thread and the composer */}
      {(emails.length > 0 || emailing) && (
        <div className="mt-4 space-y-3">
          {emails.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Email thread · {emails.length}</div>
              <EmailThread emails={emails} onReply={() => { setReplying(true); }} />
            </div>
          )}
          {emailing && (
            <EmailComposer
              key={lastInbound?.id || 'new'}
              client={client}
              storeId={storeId}
              prospect={p}
              me={me}
              initialSubject={emailScript.subject || (lastInbound?.subject ? (lastInbound.subject.startsWith('Re:') ? lastInbound.subject : `Re: ${lastInbound.subject}`) : '')}
              initialBody={emailScript.body}
              context={lastInbound?.body_text || ''}
              notify={notify}
              onClose={() => setEmailing(false)}
              onSent={async ({ subject, row, external }) => {
                if (row) setEmails((prev) => [...prev, row]);
                if (action?.kind === 'email' && !external) {
                  const sent = action.outcomes.find((o) => o.key === (action.isReply ? 'answered' : 'sent'));
                  if (sent) await logOutcome(sent, action.isReply ? 'Answered in the app' : 'Sent from the app');
                }
                setEmailing(false);
              }}
            />
          )}
        </div>
      )}

      {/* Now */}
      <div className="mt-5">
        <ActionCard prospect={p} me={me} busy={busy} onOutcome={logOutcome} note={note} setNote={setNote} dealsReady={dealsReady} reps={reps} lockedBy={me && p.rep_id && p.rep_id !== me.id ? repById?.[p.rep_id] || { name: 'Another rep' } : null} client={client} storeId={storeId} onFacts={applyFacts} hideScript={emailing && action?.kind === 'email'} inbound={lastInbound} touches={touches} />
      </div>
      <div className="mt-3">
        {replying ? (
          <ReplyDrafter client={client} storeId={storeId} prospect={p} me={me} initialText={lastInbound?.body_text || ''} onClose={() => setReplying(false)} onUseAsNote={(t) => { setNote(t); setReplying(false); }} />
        ) : p.stage !== 'lost' && (
          <button type="button" onClick={() => setReplying(true)} className="text-xs font-semibold text-violet-700 hover:underline">They wrote back? Draft a reply</button>
        )}
      </div>

      {/* Notes */}
      <div className="mt-5">
        <Field label="Notes" hint="Saved when you click away.">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={saveNotes} rows={3} className={inputCls} placeholder="Hours, who the real decision maker is, what they run today…" />
        </Field>
      </div>

      {/* Log */}
      <div className="mt-5">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Log · {touchesLoading ? (p.touch_count || 0) : touches.length} entr{(touchesLoading ? (p.touch_count || 0) : touches.length) === 1 ? 'y' : 'ies'}</span>
          <button type="button" onClick={addNote} disabled={!note.trim() || busy} className="text-xs font-semibold text-blue-700 hover:underline disabled:opacity-40" title="Save the note above without moving the row">Save as a note only</button>
        </div>
        <div className="mt-2 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {touchesLoading ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400"><RefreshCw className="w-4 h-4 animate-spin inline" /></div>
          ) : touches.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">Nothing logged yet. {p.created_at ? `Added ${when(p.created_at)}` : ''}{p.created_by ? ` by ${p.created_by}` : ''}.</div>
          ) : (
            touches.map((t) => (
              <div key={t.id} className="px-4 py-2.5 flex gap-3">
                <div className="w-20 flex-shrink-0">
                  <div className="text-xs font-bold text-slate-700">{KIND_LABEL[t.kind] || t.kind}</div>
                  <div className="text-[11px] text-slate-400">{when(t.happened_at)}</div>
                </div>
                <div className="min-w-0 flex-1 text-sm text-slate-700">
                  <div>
                    {t.outcome && <span className="font-semibold text-slate-900">{outcomeLabel(t)}</span>}
                    {t.to_stage && t.from_stage !== t.to_stage && <span className={`ml-2 px-1.5 py-0.5 rounded-full border text-[10px] font-bold ${TONE[stageOf(t.to_stage).tone]}`}>{stageOf(t.to_stage).label}</span>}
                  </div>
                  {t.note && <div className="text-slate-600 mt-0.5 break-words">{t.note}</div>}
                  {t.rep_name && <div className="text-[11px] text-slate-400 mt-0.5">{t.rep_name}</div>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between">
        <span className="text-xs text-slate-400">{p.source === 'import' ? 'From an import' : 'Added by hand'}{p.created_at ? ` · ${when(p.created_at)}` : ''}</span>
        <button type="button" onClick={remove} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /> Remove from list</button>
      </div>
    </Sheet>
  );
}

// Counts by kind, first and last contact, and every note a rep wrote
// down, so a rep opening a store sees the whole relationship in one look
// before the log below gives the order.
function HistoryStrip({ p, touches, loading }) {
  const count = (k) => touches.filter((t) => t.kind === k).length;
  const calls = count('call');
  const emails = count('email');
  const dms = count('dm');
  const texts = count('text');
  const visits = count('visit');
  const demos = count('demo');
  const real = touches.filter((t) => t.kind !== 'note' && t.kind !== 'stage' && t.kind !== 'assign');
  const first = real.length ? real[real.length - 1] : null;
  const last = real.length ? real[0] : null;
  const said = touches.filter((t) => t.note && t.note.trim());
  const reps = [...new Set(real.map((t) => t.rep_name).filter(Boolean))];
  const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const items = [
    [Phone, calls, 'call'],
    [Mail, emails, 'email'],
    [AtSign, dms, 'message'],
    [MessageSquare, texts, 'text'],
    [Footprints, visits, 'visit'],
  ].filter(([, n]) => n > 0);
  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      {loading ? (
        <div className="text-sm text-slate-400"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading the history</div>
      ) : real.length === 0 ? (
        <div className="text-sm text-slate-600"><span className="font-semibold text-slate-900">Never contacted.</span> Nobody has called, emailed or visited this store yet.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-sm font-bold text-slate-900">{real.length} touch{real.length === 1 ? '' : 'es'}</span>
            {items.map(([Icon, n, label]) => (
              <span key={label} className="inline-flex items-center gap-1.5 text-sm text-slate-700"><Icon className="w-4 h-4 text-slate-400" /><span className="font-semibold">{n}</span> {label}{n === 1 ? '' : 's'}</span>
            ))}
            {demos > 0 && <span className="text-sm text-slate-700"><span className="font-semibold">{demos}</span> demo{demos === 1 ? '' : 's'}</span>}
          </div>
          <div className="mt-1.5 text-xs text-slate-500">
            First contact {fmt(first.happened_at)} · last {fmt(last.happened_at)} by {last.rep_name || 'someone'}{reps.length > 1 ? ` · worked by ${reps.join(' and ')}` : reps.length === 1 ? ` · all by ${reps[0]}` : ''}
          </div>
        </>
      )}
      {(said.length > 0 || p.notes) && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">What they said</div>
          <ul className="mt-1.5 space-y-1">
            {said.slice(0, 5).map((t) => (
              <li key={t.id} className="text-sm text-slate-800 flex gap-2"><span className="text-slate-400 whitespace-nowrap text-xs pt-0.5">{fmt(t.happened_at)}</span><span className="min-w-0">{t.note}</span></li>
            ))}
            {said.length > 5 && <li className="text-xs text-slate-400">{said.length - 5} more in the log below</li>}
            {p.notes && <li className="text-sm text-slate-700 flex gap-2"><span className="text-slate-400 whitespace-nowrap text-xs pt-0.5">Notes</span><span className="min-w-0 whitespace-pre-wrap">{p.notes}</span></li>}
          </ul>
        </div>
      )}
    </div>
  );
}

// The outcome key back to its label. A sequence touch carries its step, so
// the label comes from that step; stage touches search the stage actions.
function outcomeLabel(t) {
  if (EXTRA_OUTCOME_LABEL[t.outcome]) return EXTRA_OUTCOME_LABEL[t.outcome];
  if (t.from_stage === 'demo' && t.step != null && DEMO_STEPS[t.step]) {
    const hit = DEMO_STEPS[t.step].outcomes.find((o) => o.key === t.outcome);
    if (hit) return hit.label;
  }
  if (t.step != null && CADENCE[t.step] && t.from_stage !== 'demo') {
    const hit = CADENCE[t.step].outcomes.find((o) => o.key === t.outcome);
    if (hit) return hit.label;
  }
  const a = STAGE_ACTIONS[t.from_stage];
  const hit = a?.outcomes.find((o) => o.key === t.outcome);
  if (hit) return hit.label;
  for (const s of [...DEMO_STEPS, ...CADENCE]) {
    const h = s.outcomes.find((o) => o.key === t.outcome);
    if (h) return h.label;
  }
  return t.outcome;
}
